//
// ============================================================================
// IMPORTS
// ============================================================================
//

// Vuetify
import { useDisplay } from "vuetify/lib/composables/display.mjs";

// Types
import type { PendingFiltersDraft, PendingSample } from "../../services/api/domains/pending/pending.types";

// Constants
import { getResultsHeaders } from "../../constants/tableHeaders";

// Feature Composables
import { useValidationFilters } from "./validation/useValidationFilters";
import { useValidationResults } from "./validation/useValidationResults";
import { useValidationSelection } from "./validation/useValidationSelection";
import { useValidationActions } from "./validation/useValidationActions";
import { useValidationKeyboard } from "./validation/useValidationKeyboard";
import { useValidationSignalR } from "./validation/useValidationSignalR";
import { useValidationPolling } from "./validation/useValidationPolling";

// Dialog Composables
import { useValidationDataFetching } from "./validation/useValidationDataFetching";
import { useValidationRefresh } from "./validation/useValidationRefresh";
import { useValidationDialogs } from "./validation/useValidationDialogs";

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Validation Page Main Orchestrator Composable
 *
 * Responsibilities:
 * - Orchestrate all validation page features and workflows
 * - Manage sample selection and navigation
 * - Coordinate result accept/reject operations
 * - Integrate real-time SignalR updates
 * - Manage dialog lifecycle and callbacks
 * - Generate component props from composed state
 * - Enforce auto-selection rules
 * - Initialize and cleanup page lifecycle
 *
 * @param key - Unique key for state isolation (default: "validation")
 */
export const useValidationPage = (key: string = "validation") => {
  //
  // ============================================================================
  // COMPOSABLES
  // ============================================================================
  //

  // Localization
  const { t } = useI18n();

  // Breakpoints
  const { smAndDown } = useDisplay();

  // Data Sources
  const pendingComposable = usePending(`${key}.pending`);
  const lisComposable = useLis(`${key}.lis`);

  // Features
  const filtersComposable = useValidationFilters(key);
  const resultsComposable = useValidationResults(key);
  const selectionComposable = useValidationSelection(key);
  const actionsComposable = useValidationActions(key);
  const pollingComposable = useValidationPolling(key);

  //
  // ============================================================================
  // STATE
  // ============================================================================
  //

  // Selection guard ID (prevents race conditions in data fetching)
  let currentSelectionId = 0;

  // Page initialization flag
  const isInitializing = ref(true);

  // Barcode update in progress (prevents race conditions)
  const isUpdatingBarcode = ref(false);

  // Barcode currently having results accepted/rejected
  const isPerformingResultAction = ref<Set<string>>(new Set());

  // Recently updated barcode (for SignalR conflict resolution)
  const barcodeJustUpdated = ref<string | null>(null);

  // Search query (cleared on navigation, persisted on reload)
  const search = usePageScopedState<string>(`${key}.search`, "");

  //
  // ============================================================================
  // SUB-COMPOSABLES (with dependencies)
  // ============================================================================
  //

  // Data fetching with race condition protection
  const dataFetchingComposable = useValidationDataFetching(
    key,
    pendingComposable,
    lisComposable,
    filtersComposable,
    selectionComposable,
    () => currentSelectionId,
  );

  //
  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================
  //

  // Table headers
  const resultsHeaders = computed(() => getResultsHeaders(t, smAndDown.value));

  /**
   * Samples sorted by priority
   *
   * Sort order:
   * 1. STAT samples first
   * 2. Oldest first
   */
  const sortedSamples = computed(() =>
    sortSamplesByPriority(pendingComposable.samples.value, pendingComposable.isStat),
  );

  // Samples filtered by search query
  const filteredSamples = computed(() => filterBySearch(sortedSamples.value, search.value || "", "barcode"));

  // User-friendly error details
  const errorDetails = computed(() => mapApiError(pendingComposable.error.value, t));

  /**
   * Check if all pending results can be accepted
   *
   * Criteria:
   * - Visit exists with examinations
   * - There are pending results
   * - Every result has a matching examination in the visit
   */
  const canAcceptAllResults = computed(() => {
    // Must have visit with examinations
    if (
      !selectionComposable.selectedSampleVisit.value?.examinations ||
      selectionComposable.selectedSampleVisit.value.examinations.length === 0
    ) {
      return false;
    }

    // Must have pending results
    if (selectionComposable.selectedSampleResults.value.length === 0) {
      return false;
    }

    // Check if all results can be published
    return selectionComposable.selectedSampleResults.value.every((result) =>
      resultsComposable.canPublishResult(result, selectionComposable.selectedSampleVisit.value),
    );
  });

  // Pending results enriched with flags and metadata
  const pendingResultsWithFlags = computed(() =>
    resultsComposable.mapPendingResults(
      selectionComposable.selectedSampleResults.value,
      selectionComposable.selectedSampleVisit.value,
      selectionComposable.selectedSampleFlagging.value,
    ),
  );

  // Current (published) results from LIS visit
  const currentResultsWithFlags = computed(() =>
    resultsComposable.mapCurrentResults(
      selectionComposable.selectedSampleVisit.value,
      selectionComposable.selectedSampleResults.value,
    ),
  );

  // Check if sample details are loading (without cache)
  const isLoadingSampleDetails = computed(() => {
    if (!selectionComposable.selectedSample.value) return false;

    const barcode = selectionComposable.selectedSample.value.barcode;
    const hasCachedResults = pendingComposable.hasCachedResultsFor(barcode);
    const hasCachedVisit = lisComposable.hasCachedVisitFor(barcode);

    return (
      (pendingComposable.resultsLoading.value && !hasCachedResults) || (lisComposable.loading.value && !hasCachedVisit)
    );
  });

  // Check if any data has been loaded
  const hasLoadedData = computed(
    () => pendingComposable.samples.value.length > 0 || pendingComposable.hasCachedSamples.value,
  );

  //
  // ============================================================================
  // SAMPLE SELECTION
  // ============================================================================
  //

  // Timer for debouncing rapid user clicks in the Explorer list
  let selectionDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  // How long to wait after the last click before starting a fetch.
  // 120ms filters accidental double-clicks and rapid scrolling-through,
  // but is imperceptible for deliberate single selections.
  const FETCH_DEBOUNCE_MS = 120;

  /**
   * Select a sample and load its data — immediate, for programmatic use
   *
   * Purpose:
   * - Change current sample selection from code (keyboard, init, post-action,
   *   SignalR navigation). Fetches data immediately without a debounce.
   *
   * Responsibilities:
   * - Cancel any pending debounced click fetch before proceeding
   * - Skip if same sample already loaded (unless forceRefresh)
   * - Increment selection guard ID (invalidates in-progress fetches)
   * - Update selection state and clear "new" indicator
   * - Fetch sample data immediately
   *
   * @param sample - Sample to select (null to clear selection)
   * @param forceRefresh - Force data refetch even if already selected
   */
  const selectSample = async (sample: PendingSample | null, forceRefresh = false): Promise<void> => {
    // Skip ONLY if same sample AND details are loaded (unless forceRefresh)
    if (
      sample &&
      selectionComposable.selectedSample.value?.barcode === sample.barcode &&
      selectionComposable.hasData.value &&
      !forceRefresh
    ) {
      return;
    }

    // Cancel any pending debounced fetch so a previous user click
    // doesn't fire after this programmatic selection completes
    if (selectionDebounceTimer !== null) {
      clearTimeout(selectionDebounceTimer);
      selectionDebounceTimer = null;
    }

    if (!sample) {
      selectionComposable.clearSelection();
      return;
    }

    currentSelectionId++;
    selectionComposable.setSelectedSample(sample);

    // TEMPORARY: Clear "new" indicator when user selects sample
    pollingComposable.clearNew(sample.barcode);

    await dataFetchingComposable.fetchSampleData(sample.barcode, forceRefresh);
  };

  /**
   * Handle a user click on a sample in the Explorer list — debounced
   *
   * Purpose:
   * - Provide instant visual feedback on click (highlight moves immediately)
   * - Prevent flooding the network when the user scrolls through samples fast
   * - Only fetch data for the sample the user actually stops on
   *
   * Why a separate function is needed (not just selectSample):
   * AbortController instances are registered inside withAbort(), which runs
   * as a microtask AFTER the current call stack unwinds. When the user clicks
   * rapidly, the next click's abortAll() fires BEFORE the previous click's
   * withAbort() has registered its controller — the abort is a no-op and both
   * requests go out in parallel. The debounce sidesteps this entirely: we never
   * start a fetch while the user is still clicking, so there is nothing to abort
   * and no race condition to guard against.
   *
   * Strategy:
   * 1. Increment selectionId & update visual immediately (instant highlight)
   * 2. Abort any requests that ARE currently in-flight (from a prior debounce)
   * 3. Cancel the previously queued debounce timer
   * 4. Schedule a new fetch after FETCH_DEBOUNCE_MS of quiet
   *
   * @param sample - Sample the user clicked
   */
  const handleSampleClick = (sample: PendingSample): void => {
    // Skip if already selected and data is loaded
    if (selectionComposable.selectedSample.value?.barcode === sample.barcode && selectionComposable.hasData.value) {
      return;
    }

    // Increment selection ID immediately — any in-progress fetch will see
    // a stale selectionId in its finally block and won't update state
    currentSelectionId++;

    // Update visual selection right away for instant feedback
    selectionComposable.setSelectedSample(sample);
    pollingComposable.clearNew(sample.barcode);

    // Paint cached data (or clear) IMMEDIATELY — before the debounce fires.
    // This eliminates the window where stale sample A data is displayed while
    // sample B is visually selected, which causes the "missing visit" alert
    // and incorrect results to flash during the debounce delay.
    const bKey = sample.barcode.toUpperCase();
    const immediateCachedResults = pendingComposable.sampleResults.value[bKey];
    const immediateHasCachedVisit = lisComposable.hasCachedVisitFor(sample.barcode);
    const immediateCachedVisit = lisComposable.visitCache.value[bKey];

    // Only use the instant-display path when the visit is confirmed non-null.
    // If the visit is cached as null (404 from a previous load), we treat it
    // as incomplete: show the overlay while the network re-validates. This
    // prevents flashing the "missing visit" alert for samples whose visit
    // may have been created since the last load (stale null cache).
    const isFullyWarm = !!(immediateCachedResults?.length && immediateHasCachedVisit && immediateCachedVisit !== null);

    if (isFullyWarm) {
      // Warm cache with confirmed visit: paint results, visit, and flagging atomically
      selectionComposable.selectedSampleResults.value = [...immediateCachedResults!];
      selectionComposable.selectedSampleVisit.value = { ...immediateCachedVisit! };
      const immediateCachedFlagging = immediateCachedVisit!.patient
        ? lisComposable.getCachedFlaggingFor(sample.barcode, immediateCachedResults!)
        : null;
      selectionComposable.selectedSampleFlagging.value = immediateCachedFlagging ?? [];
      selectionComposable.loadingSampleData.value = false;
    } else {
      // Cold, partial, or null-visit cache: clear stale data and show overlay
      selectionComposable.clearData();
      selectionComposable.loadingSampleData.value = true;
    }

    // Abort requests that are currently in-flight (started by a previous
    // debounce timer that already fired). Requests that haven't started yet
    // (mid-debounce) don't have controllers registered yet — aborting them
    // here would be a no-op, which is fine; the debounce cancellation below
    // prevents them from ever starting.
    pendingComposable.abortAll();
    lisComposable.abortAll();

    // Cancel the fetch queued by the previous click
    if (selectionDebounceTimer !== null) {
      clearTimeout(selectionDebounceTimer);
    }

    const capturedSelectionId = currentSelectionId;
    const capturedBarcode = sample.barcode;

    // Only start a fetch after the user has stopped clicking for FETCH_DEBOUNCE_MS
    selectionDebounceTimer = setTimeout(async () => {
      selectionDebounceTimer = null;
      // Guard: another click arrived during the debounce window
      if (currentSelectionId !== capturedSelectionId) return;
      await dataFetchingComposable.fetchSampleData(capturedBarcode, false);
    }, FETCH_DEBOUNCE_MS);
  };

  /**
   * Select sample by index in filtered list
   *
   * Purpose:
   * - Select sample using array index instead of object reference
   * - Enable keyboard navigation (up/down)
   * - Support post-action navigation (accept/reject)
   *
   * Responsibilities:
   * - Look up: Sample at index in filteredSamples
   * - Select: If found, call selectSample()
   * - Clear: If not found (invalid index)
   *
   * @param index - Zero-based index in filteredSamples array
   */
  const selectSampleAtIndex = async (index: number) => {
    const sample = filteredSamples.value[index];
    if (sample) {
      await selectSample(sample);
    } else {
      selectionComposable.clearSelection();
    }
  };

  //
  // ============================================================================
  // SUB-COMPOSABLES (requiring selectSample)
  // ============================================================================
  //

  // Refresh operations with debounce
  const refreshComposable = useValidationRefresh(
    key,
    pendingComposable,
    lisComposable,
    filtersComposable,
    selectionComposable,
    dataFetchingComposable.fetchSamples,
    dataFetchingComposable.fetchSampleData,
    selectSample,
    filteredSamples,
  );

  // Dialog management with callbacks
  const dialogsComposable = useValidationDialogs(
    key,
    selectionComposable,
    pendingComposable,
    lisComposable,
    filtersComposable,
    dataFetchingComposable.fetchSamples,
    selectSample,
    isUpdatingBarcode,
    barcodeJustUpdated,
    () => pollingComposable.refreshNow(silentRefresh),
  );

  // Setup reject all dialog callback
  dialogsComposable.rejectAllDialog.setConfirm(async () => {
    const sample = dialogsComposable.rejectAllDialog.selectedSample.value;
    if (!sample) return;

    await rejectAllResults();
    dialogsComposable.rejectAllDialog.close();
  });

  // Setup reject result dialog callback
  dialogsComposable.rejectResultDialog.setConfirm(async () => {
    const id = dialogsComposable.rejectResultDialog.resultId.value;
    if (id === null) return;
    dialogsComposable.rejectResultDialog.close();
    await rejectResult(id);
  });

  // SignalR real-time updates
  const signalRComposable = useValidationSignalR(
    key,
    isUpdatingBarcode,
    computed(() => filteredSamples.value),
    computed(() => selectionComposable.selectedSample.value),
    selectSample,
    dataFetchingComposable.fetchSamples,
    dataFetchingComposable.fetchSampleData,
    isPerformingResultAction,
    pendingComposable,
    selectionComposable,
  );

  // Keyboard navigation is owned by the page orchestrator so shortcuts remain
  // discoverable alongside selection/action wiring. The page component only
  // attaches/removes the global event listener.
  const keyboardComposable = useValidationKeyboard(
    filteredSamples,
    selectionComposable.selectedSample,
    selectSample,
    handleSampleClick,
  );

  //
  // ============================================================================
  // RESULT ACTIONS
  // ============================================================================
  //

  /**
   * Accept a single result
   *
   * Purpose:
   * - Publish single result to LIS
   * - Update UI optimistically (show accepted value)
   * - Navigate to next sample if all results accepted
   * - Prevent conflicts with SignalR updates
   *
   * Responsibilities:
   * - Find: Result by ID, extract test name
   * - Mark: Barcode as busy (blocks SignalR events)
   * - Call: API to publish result to LIS
   * - Update: UI to show accepted value in "Current" column
   * - Check: If all results accepted → navigate to next
   * - Unmark: Barcode as busy (allow SignalR again)
   *
   * @param resultId - ID of result to accept
   */
  const acceptResult = async (resultId: number) => {
    if (!selectionComposable.selectedSample.value) return;

    const barcode = selectionComposable.selectedSample.value.barcode;

    // Find test name
    const result = selectionComposable.selectedSampleResults.value.find((r) => r.id === resultId);
    const testName = result?.lisName || result?.testCode || "Unknown Test";

    isPerformingResultAction.value.add(barcode);

    try {
      await actionsComposable.acceptResult(
        resultId,
        testName,
        pendingComposable,
        lisComposable,
        selectionComposable,
        async (acceptedResultId: number, acceptedValue: string) => {
          // Instead of removing, mark as accepted and add current value
          const resultIndex = selectionComposable.selectedSampleResults.value.findIndex(
            (r) => r.id === acceptedResultId,
          );

          if (resultIndex !== -1) {
            const originalResult = selectionComposable.selectedSampleResults.value[resultIndex];

            // Update result to show accepted value in "Current" column
            const updatedResult = {
              ...originalResult,
              currentValue: acceptedValue,
              isAccepted: true,
              flagLevel: originalResult.flagLevel,
            };

            // Create new array with updated result
            const updatedResults = [...selectionComposable.selectedSampleResults.value];
            updatedResults[resultIndex] = updatedResult as any;
            selectionComposable.selectedSampleResults.value = updatedResults;

            // Also remove from flagging
            // if (selectionComposable.selectedSampleFlagging.value.length > 0) {
            //   selectionComposable.selectedSampleFlagging.value =
            //     selectionComposable.selectedSampleFlagging.value.filter(
            //       (f) => f.refId !== acceptedResultId.toString()
            //     );
            // }

            // Check if NO non-accepted results left in ENTIRE SAMPLE
            const hasNonAcceptedResults = selectionComposable.selectedSampleResults.value.some(
              (r: any) => !r.isAccepted,
            );

            // Only navigate when ALL results in sample are accepted
            if (!hasNonAcceptedResults) {
              refreshComposable.isRefreshing.value = true;

              const currentIndex = filteredSamples.value.findIndex((s) => s.barcode === barcode);

              await refetchSamplesAndSelectNext(currentIndex);
            }
          }
        },
      );
    } finally {
      isPerformingResultAction.value.delete(barcode);
    }
  };

  /**
   * Accept all results for current sample
   *
   * Purpose:
   * - Publish all pending results to LIS in one operation
   * - Navigate to next sample at same position
   * - Show refresh indicator during operation
   *
   * Responsibilities:
   * - Check: Current sample exists
   * - Remember: Current index before refetch
   * - Set: Refresh flag (shows loader)
   * - Call: API to accept all results
   * - Refetch: Samples list with active filters
   * - Navigate: To next sample at same index
   * - Clear: Refresh flag (hides loader)
   *
   * @throws API error if accept all fails (handled by actionsComposable)
   */
  const acceptAllResults = async () => {
    if (!selectionComposable.selectedSample.value) return;

    const currentSample = selectionComposable.selectedSample.value;

    const currentIndex = filteredSamples.value.findIndex((s) => s.barcode === currentSample.barcode);

    refreshComposable.isRefreshing.value = true;

    try {
      await actionsComposable.acceptAll(
        selectionComposable.selectedSample.value,
        pendingComposable,
        lisComposable,
        selectionComposable,
        async () => {
          // Use active filters
          await pendingComposable.fetchPendingSamples(filtersComposable.apiFilters.value, true, true);

          // Select next sample at same index
          const targetIndex = Math.min(currentIndex, filteredSamples.value.length - 1);
          await selectSampleAtIndex(targetIndex);
        },
      );
    } finally {
      refreshComposable.isRefreshing.value = false;
    }
  };

  /**
   * Reject a single result
   *
   * Purpose:
   * - Delete result from pending validation
   * - Remove from UI optimistically
   * - Navigate to next sample if no results left
   * - Prevent conflicts with SignalR updates
   *
   * Responsibilities:
   * - Find: Result by ID, extract test name
   * - Mark: Barcode as busy (blocks SignalR events)
   * - Call: API to delete result
   * - Remove: From selectedSampleResults array
   * - Remove: From selectedSampleFlagging array
   * - Check: If no results left → navigate to next
   * - Unmark: Barcode as busy (allow SignalR again)
   *
   * @param resultId - ID of result to reject
   */
  const rejectResult = async (resultId: number) => {
    if (!selectionComposable.selectedSample.value) return;

    const barcode = selectionComposable.selectedSample.value.barcode;

    // Find test name
    const result = selectionComposable.selectedSampleResults.value.find((r) => r.id === resultId);
    const testName = result?.lisName || result?.testCode || "Unknown Test";

    isPerformingResultAction.value.add(barcode);

    try {
      await actionsComposable.rejectResult(resultId, testName, pendingComposable, async (rejectedResultId: number) => {
        // Remove from pending results
        const currentResults = selectionComposable.selectedSampleResults.value;
        selectionComposable.selectedSampleResults.value = currentResults.filter((r) => r.id !== rejectedResultId);

        // Also remove from flagging
        if (selectionComposable.selectedSampleFlagging.value.length > 0) {
          selectionComposable.selectedSampleFlagging.value = selectionComposable.selectedSampleFlagging.value.filter(
            (f) => f.refId !== rejectedResultId.toString(),
          );
        }

        // If no results left, refetch and move to next
        if (selectionComposable.selectedSampleResults.value.length === 0) {
          refreshComposable.isRefreshing.value = true;

          const currentIndex = filteredSamples.value.findIndex((s) => s.barcode === barcode);

          refetchSamplesAndSelectNext(currentIndex);
        }
      });
    } finally {
      isPerformingResultAction.value.delete(barcode);
    }
  };

  /**
   * Reject all results for target sample (from dialog)
   *
   * Purpose:
   * - Delete all results for a sample in one operation
   * - Handle two scenarios: rejecting current vs other sample
   * - Navigate only if rejecting currently selected sample
   * - Show refresh indicator during operation
   *
   * Responsibilities:
   * - Get: Target sample from dialog state
   * - Check: Is target the currently selected sample?
   * - Remember: Target index before refetch
   * - Set: Refresh flag (shows loader)
   * - Call: API to reject all results
   * - Refetch: Samples list with active filters
   * - Navigate: Only if rejected current sample
   * - Clear: Refresh flag (hides loader)
   *
   * @throws API error if reject all fails (handled by actionsComposable)
   */
  const rejectAllResults = async () => {
    if (!selectionComposable.selectedSample.value) return;

    const targetSample = dialogsComposable.rejectAllDialog.selectedSample.value;
    const currentSample = selectionComposable.selectedSample.value;

    const isRejectingCurrentSample = targetSample?.barcode === currentSample.barcode;

    const currentIndex = filteredSamples.value.findIndex((s) => s.barcode === targetSample!.barcode);

    refreshComposable.isRefreshing.value = true;

    try {
      await actionsComposable.rejectAll(targetSample!, pendingComposable, async () => {
        // Use active filters
        await pendingComposable.fetchPendingSamples(filtersComposable.apiFilters.value, true, true);

        // Only change selection if we rejected the currently selected sample
        if (isRejectingCurrentSample) {
          const targetIndex = Math.min(currentIndex, filteredSamples.value.length - 1);
          await selectSampleAtIndex(targetIndex);
        }
      });
    } finally {
      refreshComposable.isRefreshing.value = false;
    }
  };

  /**
   * Check if a specific result can be accepted
   *
   * Purpose:
   * - Determine if result meets LIS publishing criteria
   * - Enable/disable accept button per result
   * - Prevent invalid publishes to LIS
   *
   * Responsibilities:
   * - Find: Result by ID in selected sample results
   * - Delegate: To resultsComposable.canPublishResult()
   * - Return: Boolean (can accept or not)
   *
   * @param resultId - Result ID to check
   * @returns True if result can be published to LIS, false otherwise
   */
  const canAcceptResult = (resultId: number): boolean => {
    const result = selectionComposable.selectedSampleResults.value.find((r) => r.id === resultId);
    if (!result) return false;

    return resultsComposable.canPublishResult(result, selectionComposable.selectedSampleVisit.value);
  };

  /**
   * Refetch samples and auto-select next at same position
   *
   * Purpose:
   * - Refresh samples list after result action
   * - Navigate to next sample at same position
   * - Clear refresh indicator when complete
   *
   * Responsibilities:
   * - Refetch: Samples with active filters (silent)
   * - Calculate: Target index (min of current or last)
   * - Select: Sample at target index
   * - Clear: Refresh flag (hide loader)
   *
   * @param currentIndex - Index of current sample before refetch
   */
  const refetchSamplesAndSelectNext = async (currentIndex: number) => {
    // Use active filters to refetch
    await dataFetchingComposable.fetchSamples(true, true);

    // Select next sample at same index
    if (filteredSamples.value.length > 0) {
      const targetIndex = Math.min(currentIndex, filteredSamples.value.length - 1);
      await selectSampleAtIndex(targetIndex);
    } else {
      selectionComposable.clearSelection();
    }

    // Clear refresh flag after navigation completes
    refreshComposable.isRefreshing.value = false;
  };

  //
  // ============================================================================
  // COMPONENT PROPS
  // ============================================================================
  //

  // Props for Explorer component (toolbar + sample list)
  const explorerProps = computed(() => ({
    signalRConnected: signalRComposable.isConnected.value,
    signalRReconnecting: signalRComposable.isEstablishingConnection.value,
    samples: filteredSamples.value,
    totalSamples: pendingComposable.samples.value.length,
    selectedBarcode: selectionComposable.selectedBarcode.value,
    loading:
      pendingComposable.loading.value &&
      !pendingComposable.isSilentRefreshing.value &&
      !pendingComposable.barcodeUpdateLoading.value,
    isRefreshing: refreshComposable.isRefreshing.value,
    error: pendingComposable.error.value,
    searchValue: search.value,
    searchQuery: search.value,
    hasActiveFilters: filtersComposable.hasFiltersActive.value,
    filteredSamplesCount: filteredSamples.value.length,
    filtersLoading: filtersComposable.baseFiltersComposable.loading.value,
    isStat: pendingComposable.isStat,
    isNewSample: pollingComposable.isNew,
    onSelect: handleSampleClick,
    onInfo: dialogsComposable.sampleInfoDialog.open,
    onEdit: dialogsComposable.editBarcodeDialog.open,
    onRejectAll: dialogsComposable.rejectAllDialog.open,
    onHighlight: (barcode: string) => highlightMatch(barcode, search.value),
    onSearchUpdate: (value: string) => (search.value = value),
    onFilter: dialogsComposable.filterSamplesDialog.open,
    onRetry: refreshComposable.refresh,
  }));

  // Props for SampleDetailsPanel component
  const sampleDetailsPanelProps = computed(() => ({
    selectedSample: selectionComposable.selectedSample.value,
    visit: selectionComposable.selectedSampleVisit.value,
    results: selectionComposable.selectedSampleResults.value,
    flagging: selectionComposable.selectedSampleFlagging.value,
    loading: pendingComposable.loading.value,
    isLoadingDetails: isLoadingSampleDetails.value,
    loadingSampleData: selectionComposable.loadingSampleData.value,
    hasLoadedData: selectionComposable.hasData.value,
    isRefreshing: refreshComposable.isRefreshing.value,
    error: pendingComposable.error.value,
    errorTitle: errorDetails.value.title,
    errorMessage: errorDetails.value.message,
    totalSamples: pendingComposable.samples.value.length,
    filteredSamplesCount: filteredSamples.value.length,
    searchQuery: search.value,
    hasActiveFilters: filtersComposable.hasFiltersActive.value,
    pendingResultsWithFlags: pendingResultsWithFlags.value,
    currentResultsWithFlags: currentResultsWithFlags.value,
    resultsHeaders: resultsHeaders.value,
    acceptingResults: actionsComposable.acceptingResults.value,
    rejectingResults: actionsComposable.rejectingResults.value,
    acceptingAll: actionsComposable.acceptingAll.value,
    rejectingAll: actionsComposable.rejectingAll.value,
    lisLoading:
      // Show loading ONLY if fetching critical data WITHOUT cache
      (pendingComposable.resultsLoading.value &&
        !pendingComposable.hasCachedResultsFor(selectionComposable.selectedSample.value?.barcode || "")) ||
      (lisComposable.loading.value &&
        !lisComposable.hasCachedVisitFor(selectionComposable.selectedSample.value?.barcode || "")),
    onRetry: refreshComposable.refresh,
    onFilter: dialogsComposable.filterSamplesDialog.open,
    onEditResult: dialogsComposable.editResultDialog.open,

    historyLoadingId: dialogsComposable.historyDialog.loadingHistoryId.value,
    canViewHistory: !!(selectionComposable.selectedSample.value && selectionComposable.selectedSampleVisit.value),
    onViewHistory: (test: { mappingKey: number; testCode: string }) => {
      if (!selectionComposable.selectedSample.value || !selectionComposable.selectedSampleVisit.value) {
        return;
      }

      dialogsComposable.historyDialog.open(
        test,
        selectionComposable.selectedSample.value.barcode,
        selectionComposable.selectedSampleVisit.value.id,
      );
    },
    onAcceptResult: acceptResult,
    onAcceptAll: acceptAllResults,
    canAcceptResult,
    onRejectResult: (resultId: number) => {
      const result = selectionComposable.selectedSampleResults.value.find((r) => r.id === resultId);
      const testName = result?.lisName || result?.testCode || "";
      const sampleType = selectionComposable.selectedSample.value?.typeName || "";
      const currentResult = result?.currentValue || "";
      const units = result?.units || "";
      const referenceRange =
        result?.rangeText ||
        (result?.minValue !== undefined && result?.maxValue !== undefined
          ? `${result.minValue} - ${result.maxValue}`
          : "");
      const resultTime = result?.localResultTime?.toString() || result?.resultTime?.toString() || "";
      const displayResult = result?.displayResult || "";
      dialogsComposable.rejectResultDialog.open(
        resultId,
        testName,
        sampleType,
        currentResult,
        units,
        referenceRange,
        resultTime,
        displayResult,
      );
    },
    onRejectAll: () => {
      if (selectionComposable.selectedSample.value) {
        dialogsComposable.rejectAllDialog.open(selectionComposable.selectedSample.value);
      }
    },
    onViewFlags: dialogsComposable.flagsDialog.open,
  }));

  //
  // ============================================================================
  // WATCHERS
  // ============================================================================
  //

  /**
   * Auto-selection enforcement
   *
   * Ensures a sample is always selected when:
   * - Samples are first loaded
   * - Search filters change sample visibility
   * - Current selection is filtered out
   *
   * Skips during:
   * - Initial page load
   * - Refresh operations
   * - Barcode updates (to prevent conflicts)
   */
  const ensureValidSelection = async (newSamples: PendingSample[]): Promise<void> => {
    // Skip during critical operations
    if (
      isInitializing.value ||
      refreshComposable.isRefreshing.value ||
      pendingComposable.loading.value ||
      selectionComposable.loadingSampleData.value ||
      isUpdatingBarcode.value
    ) {
      return;
    }

    // SKIP if user is performing actions (let them finish first)
    if (isPerformingResultAction.value.size > 0) return;

    // Case 1: No samples - clear selection
    if (!newSamples.length) {
      if (selectionComposable.selectedSample.value) {
        selectionComposable.clearSelection();
      }
      return;
    }

    const currentBarcode = selectionComposable.selectedBarcode.value;

    // Case 2: No selection - auto-select first
    if (!currentBarcode) {
      await selectSample(newSamples[0] ?? null);
      return;
    }

    // Case 3: Selection exists - check if still valid
    const stillExists = newSamples.some((s) => s.barcode === currentBarcode);
    if (!stillExists) {
      await selectSample(newSamples[0] ?? null);
    }
  };

  watch(filteredSamples, ensureValidSelection, { flush: "post" });

  // ============================================================================
  // TEMPORARY POLLING SETUP (REMOVE WHEN BACKEND SENDS FULL METADATA)
  // ============================================================================

  /**
   * TEMPORARY: Silent background refresh for new samples
   *
   * This is a workaround until SignalR sends full sample metadata.
   */
  const silentRefresh = async (): Promise<void> => {
    // SKIP if user is performing actions
    if (isPerformingResultAction.value.size > 0) {
      return;
    }

    // SKIP if user is updating barcode
    if (isUpdatingBarcode.value) {
      return;
    }

    try {
      // Fetch samples WITHOUT clearing cache (silent)
      await pendingComposable.fetchPendingSamples(filtersComposable.apiFilters.value, true, true);

      // SKIP sample data refresh if user is interacting
      if (
        selectionComposable.selectedSample.value &&
        isPerformingResultAction.value.size === 0 &&
        !isUpdatingBarcode.value
      ) {
        const barcode = selectionComposable.selectedSample.value.barcode;
        await dataFetchingComposable.fetchSampleData(barcode, true, true);
      }
    } catch (error) {
      console.error("[Silent Refresh] Failed:", error);
    }
  };

  // TEMPORARY: Start polling on mount
  onMounted(() => {
    pollingComposable.startPolling(silentRefresh);
  });

  // TEMPORARY: Stop polling on unmount
  onUnmounted(() => {
    pollingComposable.stopPolling();
  });

  // ============================================================================
  // END TEMPORARY POLLING SETUP
  // ============================================================================

  //
  // ============================================================================
  // INITIALIZATION
  // ============================================================================
  //

  /**
   * Initialize validation page
   *
   * Purpose:
   * - Load initial data and setup page state
   * - Restore persisted selection from session
   * - Auto-select first sample if no persisted selection
   * - Enable auto-selection watcher after initialization
   *
   * Responsibilities:
   * - Fetch: Initial samples with current filters
   * - Restore: Selected barcode from sessionStorage
   * - Find: Persisted sample in fetched list
   * - Select: Persisted sample or fallback to first
   * - Mark: Initialization complete (enables watcher)
   */
  const init = async () => {
    // Load samples first
    await dataFetchingComposable.fetchSamples();

    // Restore selected sample from cookie
    const persistedBarcode = selectionComposable.selectedBarcode.value;

    if (persistedBarcode && filteredSamples.value.length > 0) {
      const sampleToSelect = filteredSamples.value.find((s) => s.barcode === persistedBarcode);

      if (sampleToSelect) {
        await selectSample(sampleToSelect);
      } else if (filteredSamples.value[0]) {
        await selectSample(filteredSamples.value[0]);
      }
    } else if (filteredSamples.value[0]) {
      await selectSample(filteredSamples.value[0]);
    }

    // Mark initialization complete (watcher can now run)
    isInitializing.value = false;

    // Silent background revalidation of the selected sample's details.
    //
    // selectSample() above has a skip-guard: if the same sample is already
    // selected with data loaded it returns immediately without touching the
    // network. That is correct for normal same-page navigation, but means
    // returning from another page (e.g. Channels → Validation) never re-fetches
    // the details — the user sees potentially stale cached data indefinitely.
    //
    // After init completes we fire a silent fetch (isSilent=true) for whatever
    // sample ended up selected. isSilent:
    //   - never shows the overlay or clears the displayed data
    //   - paints updated results/visit/flags the moment fresh data arrives
    //   - is a no-op if the selection changes before the response lands
    const selectedAfterInit = selectionComposable.selectedSample.value;
    if (selectedAfterInit) {
      dataFetchingComposable.fetchSampleData(selectedAfterInit.barcode, false, true);
    }
  };

  //
  // ============================================================================
  // PUBLIC API
  // ============================================================================
  //

  return {
    // ========== State ==========
    search,
    samples: readonly(pendingComposable.samples),
    loading: readonly(pendingComposable.loading),
    error: readonly(pendingComposable.error),
    isUpdatingBarcode: readonly(isUpdatingBarcode),
    isPerformingResultAction,

    // ========== Selection ==========
    selectedSample: selectionComposable.selectedSample,
    selectedSampleResults: selectionComposable.selectedSampleResults,
    selectedSampleVisit: selectionComposable.selectedSampleVisit,
    selectedSampleFlagging: selectionComposable.selectedSampleFlagging,
    loadingSampleData: selectionComposable.loadingSampleData,

    // ========== Actions ==========
    acceptingResults: actionsComposable.acceptingResults,
    rejectingResults: actionsComposable.rejectingResults,
    acceptingAll: actionsComposable.acceptingAll,
    rejectingAll: actionsComposable.rejectingAll,

    // ========== Computed ==========
    filteredSamples,
    pendingResultsWithFlags,
    currentResultsWithFlags,
    hasLoadedData,
    canAcceptAllResults,

    // ========== Component Props ==========
    explorerProps,
    sampleDetailsPanelProps,
    dialogProps: dialogsComposable.dialogProps,

    // ========== Methods ==========
    refreshAllData: refreshComposable.refresh,
    init,
    removeFilter: (type: keyof PendingFiltersDraft, value: number) => {
      filtersComposable.removeFilter(type, value);
      dataFetchingComposable.fetchSamples();
    },
    selectSample,
    handleSampleClick,
    handleKeyDown: keyboardComposable.handleKeyDown,
    fetchSamples: dataFetchingComposable.fetchSamples,
    fetchSampleData: dataFetchingComposable.fetchSampleData,

    // ========== SignalR ==========
    isConnected: signalRComposable.isConnected,
    connectionState: signalRComposable.connectionState,
    isConnecting: signalRComposable.isConnecting,
    isReconnecting: signalRComposable.isReconnecting,
    isEstablishingConnection: signalRComposable.isEstablishingConnection,
    initializeSignalR: signalRComposable.initializeSignalR,
    cleanupSignalR: signalRComposable.cleanupSignalR,

    // ============================================================================
    // TEMPORARY POLLING API (REMOVE WHEN BACKEND FIXED)
    // ============================================================================

    // Polling state
    pollingState: computed(() => ({
      needsPolling: pollingComposable.needsPolling.value,
      nextRefreshIn: pollingComposable.nextRefreshIn.value,
      lastRefreshTime: pollingComposable.lastRefreshTime.value,
    })),

    // Check if sample is new
    isNewSample: pollingComposable.isNew,

    // Manual refresh (e.g., after barcode change)
    triggerSilentRefresh: () => pollingComposable.refreshNow(silentRefresh),

    // ============================================================================
    // END TEMPORARY POLLING API
    // ============================================================================
  };
};
